import React, { useState } from 'react';
import moment, { Moment } from 'moment';
import SectionContainer from '@common/components/SectionContainer';
import Markdown from '@common/components/Markdown';
import { DiscountBanner, FromPrice } from '@common/components/Price';
import { Spinner } from '@common/components/Spinner';
import { Button } from '@common/components';
import { Heading4 } from '@common/components/Typography';
import { DatePicker } from '@features/JourneyPicker';
import ImgixWrapper from '@common/components/ImgixWrapper';
import { hasAvailability } from '@utils/helpers';
import {
  useGetSpaTreatments,
  useGetSpaTreatmentsPage,
  useGetSpaCategories,
} from '@features/spa/hooks';
import { useTranslations } from '@lang';
import { Link } from 'shared-routing';
import { Routes } from '@common/constants';
import { Property } from 'shared-types/Property';
import {
  TreatmentsCategoryWrapper,
  TreatmentRow,
  TreatmentListingWrapper,
  TreatmentListing,
  ButtonColumn,
  NameColumn,
  PriceColumn,
  TreatmentPagePreview,
  TreatmentPageDescription,
  MobileColumn,
} from './styles';
import { Category } from 'shared-types';

export interface JourneyParams {
  fromDate: Moment;
  toDate?: Moment;
  spaDate?: Moment;
}

export interface SpaTreatmentsCategoryProps {
  changeJourneyParam: (param: string, value: any) => void;
  property: Property;
  journeyParams: JourneyParams;
  category: Category;
  language: string;
}

export const SpaTreatmentsCategory: React.FC<SpaTreatmentsCategoryProps> = ({
  journeyParams,
  changeJourneyParam,
  property,
  category,
  language,
}) => {
  const [date, setDate] = useState<Moment>(journeyParams.fromDate);
  const { spaTreatments = [], isFetching: isFetchingTreatments } = useGetSpaTreatments(
    property.id,
    language,
    journeyParams.spaDate?.format('yyyy-MM-DD') || null,
    Number(category.id),
    undefined,
    true
  );
  const { data: spaTreatmentsPage, isFetching: isFetchingTreatmentsPage } = useGetSpaTreatmentsPage(
    property.id,
    language
  );

  const { data: treatmentsCategories, isFetching: isFetchingCategories } = useGetSpaCategories(
    property.id,
    'treatments',
    language
  );

  const isFetching = isFetchingTreatments || isFetchingTreatmentsPage || isFetchingCategories;
  const { t } = useTranslations();

  const handleArrivalDateChange = (): void => {
    changeJourneyParam('fromDate', date);
    changeJourneyParam('spaDate', date);
  };

  const currentCategory = (treatmentsCategories || []).find(
    ({ id }) => String(id) === String(category.id)
  );

  return (
    <TreatmentsCategoryWrapper>
      <Spinner isActive={isFetching} showDelayMessage />
      <SectionContainer>
        <DatePicker
          fromDate={moment(journeyParams.fromDate)}
          onDateChange={setDate}
          handleUpdate={handleArrivalDateChange}
        />
      </SectionContainer>
      {currentCategory && (
        <SectionContainer>
          <Heading4 tag="h1">{currentCategory.name}</Heading4>
          <TreatmentListingWrapper>
            <TreatmentListing>
              {spaTreatments.map((treatment) => {
                const isAvailable = hasAvailability(treatment, journeyParams.fromDate);
                return (
                  <TreatmentRow key={treatment.id} isAvailable={isAvailable}>
                    <MobileColumn>
                      <NameColumn>{treatment.name}</NameColumn>
                      <PriceColumn>
                        <DiscountBanner discount={treatment.discount} isPlaced={false} />
                        <FromPrice {...treatment} />
                      </PriceColumn>
                    </MobileColumn>
                    <ButtonColumn>
                      <Link
                        route={Routes.SPA_TREATMENT_DETAIL}
                        params={{
                          id: treatment.id,
                        }}
                      >
                        <Button isPrimary={isAvailable} isSmall>
                          {isAvailable ? t('common.moreDetails') : t('common.editSearch')}
                        </Button>
                      </Link>
                    </ButtonColumn>
                  </TreatmentRow>
                );
              })}
            </TreatmentListing>
            <TreatmentPagePreview>
              {(category.image?.url || spaTreatmentsPage?.heroBackgroundImage) && (
                <ImgixWrapper
                  src={(category.image?.url || spaTreatmentsPage?.heroBackgroundImage?.url) ?? ''}
                  width={426}
                  height={284}
                  alt={currentCategory.name}
                />
              )}
              <TreatmentPageDescription>
                <Markdown text={category.description || spaTreatmentsPage?.content} />
              </TreatmentPageDescription>
            </TreatmentPagePreview>
          </TreatmentListingWrapper>
        </SectionContainer>
      )}
    </TreatmentsCategoryWrapper>
  );
};
